import { api } from "@/lib/api";

const CODES = {
  0: ["Ciel dégagé", "sun"], 1: ["Peu nuageux", "sun"], 2: ["Partiellement nuageux", "cloud-sun"],
  3: ["Couvert", "cloud"], 45: ["Brouillard", "cloud-fog"], 48: ["Brouillard givrant", "cloud-fog"],
  51: ["Bruine légère", "cloud-drizzle"], 53: ["Bruine", "cloud-drizzle"], 55: ["Bruine dense", "cloud-drizzle"],
  61: ["Pluie faible", "cloud-rain"], 63: ["Pluie", "cloud-rain"], 65: ["Forte pluie", "cloud-rain"],
  71: ["Neige faible", "cloud-snow"], 73: ["Neige", "cloud-snow"], 75: ["Forte neige", "cloud-snow"],
  80: ["Averses", "cloud-rain"], 81: ["Averses modérées", "cloud-rain"], 82: ["Fortes averses", "cloud-rain"],
  95: ["Orage", "cloud-lightning"], 96: ["Orage grêle", "cloud-lightning"], 99: ["Orage violent", "cloud-lightning"],
};

const cond = (code) => {
  const [label, icon] = CODES[code] || ["-", "cloud"];
  return { label, icon };
};

const TTL = 30 * 60 * 1000;

const fromOpenMeteo = async (lat, lon) => {
  const params = new URLSearchParams({
    latitude: lat, longitude: lon,
    current: "temperature_2m,wind_speed_10m,weather_code,surface_pressure,relative_humidity_2m",
    daily: "temperature_2m_max,temperature_2m_min,weather_code",
    timezone: "Europe/Paris", forecast_days: 4,
  });
  const res = await fetch(`https://api.open-meteo.com/v1/forecast?${params}`);
  if (!res.ok) throw new Error(`open-meteo ${res.status}`);
  const d = await res.json();
  const c = d.current || {};
  const daily = d.daily || {};
  return {
    temperature: c.temperature_2m,
    wind_speed: c.wind_speed_10m,
    pressure: c.surface_pressure,
    humidity: c.relative_humidity_2m,
    condition: cond(c.weather_code),
    forecast: (daily.time || []).map((date, i) => ({
      date,
      t_max: daily.temperature_2m_max[i],
      t_min: daily.temperature_2m_min[i],
      condition: cond(daily.weather_code[i]),
    })),
  };
};

const metCond = (symbol = "") => {
  const s = symbol.replace(/_(day|night|polartwilight)$/, "");
  if (s.includes("thunder")) return { label: "Orage", icon: "cloud-lightning" };
  if (s.includes("snow") || s.includes("sleet")) return { label: "Neige", icon: "cloud-snow" };
  if (s.includes("heavyrain")) return { label: "Forte pluie", icon: "cloud-rain" };
  if (s.includes("showers")) return { label: "Averses", icon: "cloud-rain" };
  if (s.includes("lightrain")) return { label: "Pluie faible", icon: "cloud-drizzle" };
  if (s.includes("rain")) return { label: "Pluie", icon: "cloud-rain" };
  if (s === "fog") return { label: "Brouillard", icon: "cloud-fog" };
  if (s === "cloudy") return { label: "Couvert", icon: "cloud" };
  if (s === "partlycloudy") return { label: "Partiellement nuageux", icon: "cloud-sun" };
  if (s === "fair") return { label: "Peu nuageux", icon: "sun" };
  return { label: "Ciel dégagé", icon: "sun" };
};

const fromMetNo = async (lat, lon) => {
  const res = await fetch(`https://api.met.no/weatherapi/locationforecast/2.0/compact?lat=${lat.toFixed(4)}&lon=${lon.toFixed(4)}`);
  if (!res.ok) throw new Error(`met.no ${res.status}`);
  const ts = (await res.json()).properties.timeseries;
  const now = ts[0].data;
  const det = now.instant.details;
  const days = {};
  ts.forEach((t) => {
    const date = new Date(t.time).toLocaleDateString("en-CA", { timeZone: "Europe/Paris" });
    const temp = t.data.instant.details.air_temperature;
    const d = (days[date] ||= { date, t_max: temp, t_min: temp, symbol: null });
    d.t_max = Math.max(d.t_max, temp);
    d.t_min = Math.min(d.t_min, temp);
    const sym = t.data.next_6_hours?.summary?.symbol_code;
    if (sym && (!d.symbol || new Date(t.time).getUTCHours() === 10)) d.symbol = sym;
  });
  return {
    temperature: det.air_temperature,
    wind_speed: Math.round(det.wind_speed * 3.6 * 10) / 10,
    pressure: det.air_pressure_at_sea_level,
    humidity: det.relative_humidity,
    condition: metCond((now.next_1_hours || now.next_6_hours)?.summary?.symbol_code),
    forecast: Object.values(days).slice(0, 4).map((d) => ({ date: d.date, t_max: d.t_max, t_min: d.t_min, condition: metCond(d.symbol) })),
  };
};

export const getWeather = async (lat, lon) => {
  const key = `weather_${lat.toFixed(2)}_${lon.toFixed(2)}`;
  try {
    const cached = JSON.parse(sessionStorage.getItem(key));
    if (cached && Date.now() - cached.t < TTL) return cached.data;
  } catch {}
  let data;
  try {
    data = await fromOpenMeteo(lat, lon);
  } catch {
    try {
      data = await fromMetNo(lat, lon);
    } catch {
      data = (await api.get("/weather", { params: { lat, lon } })).data;
    }
  }
  sessionStorage.setItem(key, JSON.stringify({ t: Date.now(), data }));
  return data;
};
